import { ref, nextTick, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { useKeyCountManager, updateSelectionSafe } from '@/views/editor/composable/useEditorHelpers.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { normalizeBlock, waitForStyleComplete } from '@/views/editor/composable/applyFontStyle.ts'
import { TypingHistoryManager } from '@/views/editor/composable/useTypingHistory.ts'
import { socketSave } from '@/views/editor/composable/useSocketSave.ts'

interface UseEditorInputParams {
	history: UseHistory
	noteUid: Ref<string>
	noteType: Ref<string>
	selector: Ref<any>
	socket: Ref<any>
	deletePrefix: (id: string) => string
	blockEvent: (event: KeyboardEvent) => Promise<void>
}

let _editorTypingHistoryManager: TypingHistoryManager | null = null

export function getEditorTypingHistoryManager() {
	return _editorTypingHistoryManager
}

export const useEditorInput = (params: UseEditorInputParams) => {
	const { history, noteUid, noteType, selector, socket, deletePrefix, blockEvent } = params
	const keyCountManager = useKeyCountManager()
	const typingHistoryManager = new TypingHistoryManager(history)
	_editorTypingHistoryManager = typingHistoryManager
	
	let skipNextInput = false
	
	const handleBeforeInput = (event: InputEvent) => {
		if (event.inputType !== 'insertText' && event.inputType !== 'insertCompositionText') {
			return
		}

		const sel = window.getSelection()
		if (sel && sel.rangeCount > 0) {
			const range = sel.getRangeAt(0)
			const container = range.commonAncestorContainer
			const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement
			
			if (element) {
				const editableElement = element.closest('[contenteditable="true"]')
				if (editableElement) {
					return
				}
				
				const blockElement = element.closest('table, img, .editor--file')
				if (blockElement) {
					event.preventDefault()
					return
				}
			}
		}
	}

	const handleInput = async (event: Event) => {
		if (skipNextInput) {
			skipNextInput = false
			return
		}

		// IME 조합 중에는 DOM 조작을 건너뜀 (조합 노드가 깨지는 것 방지)
		if (keyCountManager.isComposing.value || (event as InputEvent).isComposing) {
			return
		}

		const target = event.target as HTMLElement
		if (target?.closest('.image--caption__title') || target?.closest('.image--caption__description')) {
			return
		}
		
		const INVISIBLE = '\u2060'
		const INVISIBLE_RE = /[\u200B-\u200D\uFEFF\u00A0\u2060]/g
		const INVISIBLE_MARKER_RE = /[\u200B-\u200D\uFEFF\u2060]/g
		const sel = window.getSelection()
		if (!sel || sel.rangeCount === 0) return
		
		const selNode = sel.anchorNode
		const selElement = selNode?.nodeType === Node.TEXT_NODE ? selNode.parentElement : selNode as HTMLElement
		const cellElement = selElement?.closest('td')
		if (cellElement && !cellElement.classList.contains('number--line')) {
			return
		}
		
		const range = sel.getRangeAt(0)
		const node = range.startContainer
		const offset = range.startOffset
		
		if (node.nodeType === Node.TEXT_NODE) {
			const textContent = node.textContent || ''
			const parent = node.parentElement
			
			if (parent) {
				if (parent.classList.contains('editor--block')) {
					const prev = node.previousSibling
					const next = node.nextSibling
					let targetSpan: HTMLSpanElement | null = null
					let isNextSpan = false
					
					if (prev && prev.nodeType === Node.ELEMENT_NODE && (prev as Element).tagName === 'SPAN') {
						targetSpan = prev as HTMLSpanElement
						isNextSpan = false
					} else if (next && next.nodeType === Node.ELEMENT_NODE && (next as Element).tagName === 'SPAN') {
						targetSpan = next as HTMLSpanElement
						isNextSpan = true
					}
					
					if (targetSpan) {
						const span = targetSpan
						const spanText = span.textContent || ''
						const hasCursorMarker = span.hasAttribute('data-cursor-marker')
						const endsWithInvisible = spanText.endsWith(INVISIBLE)
						const hasInlineStyle = span.style.cssText && span.style.cssText.trim() !== ''
						const hasClassName = span.className && span.className.trim() !== ''
						const hasAnyStyle = hasInlineStyle || hasClassName
						
						if (isNextSpan) {
							const spanTextWithoutInvisible = spanText.replace(INVISIBLE_RE, '')
							if (spanTextWithoutInvisible.length > 0) {
								return
							}
						}
						
						if (hasAnyStyle && (endsWithInvisible || hasCursorMarker)) {
							const textNode = span.firstChild
							if (textNode && textNode.nodeType === Node.TEXT_NODE) {
								const cleanedSpanText = endsWithInvisible ? spanText.slice(0, -1) : spanText
								
								if (isNextSpan) {
									textNode.textContent = textContent + cleanedSpanText
								} else {
									textNode.textContent = cleanedSpanText + textContent
								}
								
								node.parentNode?.removeChild(node)
								
								if (hasCursorMarker) {
									span.removeAttribute('data-cursor-marker')
								}
								
								const newRange = document.createRange()
								const cursorPos = isNextSpan ? textContent.length : textNode.textContent.length
								newRange.setStart(textNode, cursorPos)
								newRange.collapse(true)
								sel.removeAllRanges()
								sel.addRange(newRange)
								return
							}
						}
					}
				}
				
				if (parent.tagName === 'SPAN') {
					const span = parent as HTMLSpanElement
					
					const hasInlineStyle = span.style.cssText && span.style.cssText.trim() !== ''
					const hasClassName = span.className && span.className.trim() !== ''
					const hasAnyStyle = hasInlineStyle || hasClassName
					const containsInvisible = textContent.includes(INVISIBLE)
					const hasCursorMarker = span.hasAttribute('data-cursor-marker')
					
					if (hasAnyStyle && containsInvisible) {
						const cleanedText = textContent.replace(INVISIBLE_MARKER_RE, '')
						const cursorOffset = offset > 0 ? offset - (textContent.substring(0, offset).match(INVISIBLE_MARKER_RE) || []).length : 0
						
						if (cleanedText.length > 0) {
							const endsWithSpace = cleanedText !== cleanedText.trimEnd()
							const finalText = endsWithSpace ? cleanedText + INVISIBLE : cleanedText
							
							node.textContent = finalText
							
							if (cleanedText.trim() !== '') {
								span.removeAttribute('data-cursor-marker')
							}
							
							const newRange = document.createRange()
							newRange.setStart(node, Math.min(cursorOffset, cleanedText.length))
							newRange.collapse(true)
							sel.removeAllRanges()
							sel.addRange(newRange)
							return
						}
					}
				}
			}
		}
		
		if (node.nodeType === Node.ELEMENT_NODE) {
			const siblings = Array.from(node.childNodes)
			const prevNode = siblings[offset - 1]
			const nextNode = siblings[offset]
			
			if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE && (prevNode as Element).tagName === 'SPAN') {
				const span = prevNode as HTMLSpanElement
				const spanText = span.textContent || ''
				
				const hasCursorMarker = span.hasAttribute('data-cursor-marker')
				const endsWithInvisible = spanText.endsWith(INVISIBLE)
				const hasInlineStyle = span.style.cssText && span.style.cssText.trim() !== ''
				const hasClassName = span.className && span.className.trim() !== ''
				const hasAnyStyle = hasInlineStyle || hasClassName
				
				if (hasAnyStyle && (endsWithInvisible || hasCursorMarker) && nextNode && nextNode.nodeType === Node.TEXT_NODE) {
					const textContent = nextNode.textContent || ''
					const textNode = span.firstChild
					
					if (textNode && textNode.nodeType === Node.TEXT_NODE) {
						const cleanedSpanText = endsWithInvisible ? spanText.slice(0, -1) : spanText
						textNode.textContent = cleanedSpanText + textContent
						if (nextNode.parentNode) {
							nextNode.parentNode.removeChild(nextNode)
						}
						
						if (hasCursorMarker) {
							span.removeAttribute('data-cursor-marker')
						}
						
						const newRange = document.createRange()
						newRange.setStart(textNode, textNode.textContent.length)
						newRange.collapse(true)
						sel.removeAllRanges()
						sel.addRange(newRange)
					}
				}
			}
		}
		
		// 입력 후 orphaned fontSize 정리
		const blockEl = selElement?.closest('.editor--block') as HTMLElement | null
		if (blockEl) {
			const checkOrphanedSpans = () => {
				const spans = blockEl.querySelectorAll('span')
				spans.forEach(span => {
					const htmlSpan = span as HTMLSpanElement
					if (htmlSpan.hasAttribute('data-latex') || htmlSpan.closest('[data-latex]')) return
					const hasVerticalAlign = htmlSpan.style.verticalAlign === 'super' || htmlSpan.style.verticalAlign === 'sub'
					const hasFontSize = !!htmlSpan.style.fontSize
					
					if (!hasVerticalAlign && hasFontSize) {
						const fontSize = parseFloat(htmlSpan.style.fontSize)
						const computed = window.getComputedStyle(blockEl)
						const baseFontSize = parseFloat(computed.fontSize) || 14
						const expectedOrphanedSize = Math.round(baseFontSize * 0.75 * 10) / 10
						
						if (Math.abs(fontSize - expectedOrphanedSize) < 0.1) {
							htmlSpan.style.removeProperty('font-size')
						}
					}
				})
			}
			
			await typingHistoryManager.onInput(event as InputEvent)
			
			checkOrphanedSpans()
			setTimeout(checkOrphanedSpans, 0)
			
			normalizeBlock(blockEl)

			// normalizeBlock의 DOM 조작으로 active가 빠질 수 있으므로 복원
			if (blockEl.dataset.type === 'text' && !blockEl.classList.contains('active')) {
				blockEl.classList.add('active')
			}
			
			if ((event as InputEvent).inputType?.startsWith('delete')) {
				try {
					let absOffset = -1
					const curSel = window.getSelection()
					
					if (curSel?.isCollapsed && curSel.rangeCount > 0) {
						const curRange = curSel.getRangeAt(0)
						
						try {
							const measRange = document.createRange()
							measRange.setStart(blockEl, 0)
							measRange.setEnd(curRange.startContainer, curRange.startOffset)
							absOffset = measRange.toString().length
						} catch {}
						
						const curEl = curRange.startContainer.nodeType === Node.TEXT_NODE
							? curRange.startContainer.parentElement
							: curRange.startContainer as HTMLElement
						if (curEl?.tagName === 'SPAN' && curEl.closest('.editor--block') === blockEl) {
							const spanText = curEl.textContent || ''
							if (!spanText || /^[\u200B-\u200D\uFEFF\u00A0\u2060]+$/.test(spanText)) {
								if (curEl.style.cssText?.trim()) {
									curEl.remove()
								}
							}
						}
					}
					
					blockEl.normalize()
					
					if (absOffset >= 0) {
						const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT)
						let acc = 0
						let nd: Node | null
						while ((nd = walker.nextNode())) {
							const len = (nd.textContent || '').length
							if (acc + len >= absOffset) {
								const sel2 = window.getSelection()
								if (sel2) {
									const nr = document.createRange()
									nr.setStart(nd, Math.min(absOffset - acc, len))
									nr.collapse(true)
									sel2.removeAllRanges()
									sel2.addRange(nr)
								}
								break
							}
							acc += len
						}
					}
				} catch {}
			}
		}
	}
	
	let compositionTimeout: ReturnType<typeof setTimeout> | null = null
	let hasCompositionContent = false
	
	// orphaned fontSize를 체크하는 함수
	const checkAndRemoveOrphanedFontSize = (span: HTMLSpanElement) => {
		const hasVerticalAlign = span.style.verticalAlign === 'super' || span.style.verticalAlign === 'sub'
		const hasFontSize = !!span.style.fontSize
		
		if (!hasVerticalAlign && hasFontSize) {
			const blockEl = span.closest('.editor--block') as HTMLElement | null
			if (blockEl) {
				const fontSize = parseFloat(span.style.fontSize)
				const computed = window.getComputedStyle(blockEl)
				const baseFontSize = parseFloat(computed.fontSize) || 14
				const expectedOrphanedSize = Math.round(baseFontSize * 0.75 * 10) / 10
				
				if (Math.abs(fontSize - expectedOrphanedSize) < 0.1) {
					span.style.removeProperty('font-size')
				}
			}
		}
	}
	
	// MutationObserver로 span 생성 즉시 감지
	let mutationObserver: MutationObserver | null = null
	const setupOrphanedFontSizeObserver = (editorContainer: HTMLElement) => {
		if (mutationObserver) {
			mutationObserver.disconnect()
		}
		
		mutationObserver = new MutationObserver((mutations) => {
			if (keyCountManager.isComposing.value) return
			
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						const element = node as HTMLElement
						
						if (element.tagName === 'SPAN') {
							checkAndRemoveOrphanedFontSize(element as HTMLSpanElement)
						}
						
						const spans = element.querySelectorAll('span')
						spans.forEach(span => {
							checkAndRemoveOrphanedFontSize(span as HTMLSpanElement)
						})
					}
				})
			})
		})
		
		mutationObserver.observe(editorContainer, {
			childList: true,
			subtree: true
		})
	}

	const handleCompositionStart = async () => {
		keyCountManager.handleCompositionStart()
		hasCompositionContent = true

		await typingHistoryManager.onRegularKey()

		if (compositionTimeout) {
			clearTimeout(compositionTimeout)
			compositionTimeout = null
		}
	}

	const handleCompositionEnd = async () => {
		keyCountManager.handleCompositionEnd()
		hasCompositionContent = false
		
		await nextTick()
		
		const INVISIBLE = '\u2060'
		const sel = window.getSelection()
		if (sel && sel.rangeCount > 0) {
			const range = sel.getRangeAt(0)
			const node = range.startContainer
			
			if (node.nodeType === Node.TEXT_NODE) {
				const textContent = node.textContent || ''
				const parent = node.parentElement
				
				if (parent && parent.classList.contains('editor--block')) {
					const prev = node.previousSibling
					const next = node.nextSibling
					let targetSpan: HTMLSpanElement | null = null
					
					if (prev && prev.nodeType === Node.ELEMENT_NODE && (prev as Element).tagName === 'SPAN') {
						targetSpan = prev as HTMLSpanElement
					} else if (next && next.nodeType === Node.ELEMENT_NODE && (next as Element).tagName === 'SPAN') {
						targetSpan = next as HTMLSpanElement
					}
					
					if (targetSpan) {
						const span = targetSpan
						const spanText = span.textContent || ''
						
						const hasInlineStyle = span.style.cssText && span.style.cssText.trim() !== ''
						const hasClassName = span.className && span.className.trim() !== ''
						const hasAnyStyle = hasInlineStyle || hasClassName
						const endsWithInvisible = spanText.endsWith(INVISIBLE)
						
						if (hasAnyStyle && endsWithInvisible) {
							const textNode = span.firstChild
							if (textNode && textNode.nodeType === Node.TEXT_NODE) {
								const cleanedSpanText = spanText.slice(0, -1)
								textNode.textContent = cleanedSpanText + textContent
								node.parentNode?.removeChild(node)
								
								const newRange = document.createRange()
								newRange.setStart(textNode, textNode.textContent.length)
								newRange.collapse(true)
								sel.removeAllRanges()
								sel.addRange(newRange)
								
								skipNextInput = true
							}
						}
					}
				}
			}
		}
		
		// Composition 후 orphaned fontSize 정리
		const sel2 = window.getSelection()
		const selElement2 = sel2?.anchorNode?.nodeType === Node.TEXT_NODE
			? sel2.anchorNode.parentElement
			: sel2?.anchorNode as HTMLElement | null
		const blockEl2 = selElement2?.closest('.editor--block') as HTMLElement | null
		
		if (blockEl2) {
			const spans = blockEl2.querySelectorAll('span')
			spans.forEach(span => {
				const htmlSpan = span as HTMLSpanElement
				const hasVerticalAlign = htmlSpan.style.verticalAlign === 'super' || htmlSpan.style.verticalAlign === 'sub'
				const hasFontSize = !!htmlSpan.style.fontSize
				
				if (!hasVerticalAlign && hasFontSize) {
					const fontSize = parseFloat(htmlSpan.style.fontSize)
					const computed = window.getComputedStyle(blockEl2)
					const baseFontSize = parseFloat(computed.fontSize) || 14
					const expectedOrphanedSize = Math.round(baseFontSize * 0.75 * 10) / 10
					
					if (Math.abs(fontSize - expectedOrphanedSize) < 0.1) {
						htmlSpan.style.removeProperty('font-size')
					}
				}
			})
		}
		
		setTimeout(async () => {
			const sel = window.getSelection()
			if (sel && sel.rangeCount > 0) {
				const range = sel.getRangeAt(0)
				const node = range.startContainer
				if (node.nodeType === Node.TEXT_NODE && node.textContent) {
					const offset = range.startOffset
					const charBefore = node.textContent[offset - 1]
					const charAt = node.textContent[offset]
					const charBeforeCode = charBefore?.charCodeAt(0)
					const charAtCode = charAt?.charCodeAt(0)
					
					if (charBefore === ' ' || charBeforeCode === 32 || charBeforeCode === 160) {
						await typingHistoryManager.onInput({ data: ' ' } as InputEvent)
					} else if (charAt === ' ' || charAtCode === 32 || charAtCode === 160) {
						await typingHistoryManager.onInput({ data: ' ' } as InputEvent)
					}
				}
			}
		}, 0)
	}

	const forceEndCompositionIfNeeded = async () => {
		if (keyCountManager.isComposing.value || hasCompositionContent) {
			const activeElement = document.activeElement
			if (activeElement && activeElement instanceof HTMLElement) {
				const compositionEvent = new CompositionEvent('compositionend', {
					data: '',
					bubbles: true,
					cancelable: true,
				})
				activeElement.dispatchEvent(compositionEvent)
				await nextTick()
				await new Promise(resolve => setTimeout(resolve, 50))
			}

			hasCompositionContent = false
			keyCountManager.resetKeyCount()
		}
	}

	const handleGlobalUndo = async (event: KeyboardEvent) => {
		
		const target = event.target as HTMLElement
		
		const isTableCell = target.tagName === 'TD' || target.closest('td')
		
		if (!isTableCell && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
			return
		}
		
		const hasOpenModal = document.querySelector('.modal--wrap:not([style*="display: none"])')
		if (hasOpenModal) {
			return
		}
		
		const isZKey = event.key === 'z' || event.key === 'Z' || event.code === 'KeyZ'
		const isYKey = event.key === 'y' || event.key === 'Y' || event.code === 'KeyY'
		
		if ((event.ctrlKey || event.metaKey) && isZKey && !event.shiftKey) {
			event.preventDefault()
			event.stopPropagation()
			
			try {
				await waitForStyleComplete()
				
				const wasComposing = keyCountManager.isComposing.value
				forceEndCompositionIfNeeded()
				
				if (wasComposing) {
					await nextTick()
					await new Promise(requestAnimationFrame)
					await new Promise(requestAnimationFrame)
				}
				
				await typingHistoryManager.saveCurrentState()
				await history.undo()
			} catch (e) {
				console.error('[handleGlobalUndo] undo error:', e)
			}
			return
		}
		
		if ((event.ctrlKey || event.metaKey) && isZKey && event.shiftKey) {
			event.preventDefault()
			event.stopPropagation()
			
			try {
				await waitForStyleComplete()
				
				const wasComposing = keyCountManager.isComposing.value
				forceEndCompositionIfNeeded()
				
				if (wasComposing) {
					await nextTick()
					await new Promise(requestAnimationFrame)
					await new Promise(requestAnimationFrame)
				}
				
				await typingHistoryManager.saveCurrentState()
				await history.redo()
			} catch (e) {
				console.error('[handleGlobalUndo] redo error:', e)
			}
			return
		}
		
		if ((event.ctrlKey || event.metaKey) && isYKey) {
			event.preventDefault()
			event.stopPropagation()
			
			try {
				await waitForStyleComplete()
				
				const wasComposing = keyCountManager.isComposing.value
				forceEndCompositionIfNeeded()
				
				if (wasComposing) {
					await nextTick()
					await new Promise(requestAnimationFrame)
					await new Promise(requestAnimationFrame)
				}
				
				await typingHistoryManager.saveCurrentState()
				await history.redo()
			} catch (e) {
				console.error('[handleGlobalUndo] redo(Ctrl+Y) error:', e)
			}
			return
		}
		
	}

	const changeInput = async (event: KeyboardEvent) => {
		if (event.isComposing || keyCountManager.isComposing.value) {
			return
		}

		const target = event.target as HTMLElement

		const isInTable = !!target.closest('table')
		const isFormattingShortcut = !isInTable && (event.ctrlKey || event.metaKey) && (
			['b', 'i', 'u'].includes(event.key.toLowerCase()) ||
			(event.shiftKey && event.key.toLowerCase() === 's')
		)
		if (isFormattingShortcut) {
			event.preventDefault()
			event.stopPropagation()
		}

		if (noteType.value === 'Project' && selector.value?.id && selector.value.id.startsWith('prefix-')) {
			socket.value.blockEdit(noteUid.value, deletePrefix(selector.value.id))
		}

		updateSelectionSafe()

		if (target.closest('table')) {
			if (event.key === 'Backspace' || event.key === 'Delete') {
				const sel = window.getSelection()
				if (sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed) {
					const r = sel.getRangeAt(0)
					const sEl = r.startContainer.nodeType === Node.TEXT_NODE ? r.startContainer.parentElement : r.startContainer as HTMLElement
					const eEl = r.endContainer.nodeType === Node.TEXT_NODE ? r.endContainer.parentElement : r.endContainer as HTMLElement
					const sBlock = sEl?.closest('.editor--block')
					const eBlock = eEl?.closest('.editor--block')
					if (sBlock && eBlock && sBlock !== eBlock) {
						// 크로스 블록 선택 → deleteButtonEvent에서 처리하도록 진행
					} else {
						return
					}
				} else {
					return
				}
			} else {
				return
			}
		}

		const blockType = target.closest('.editor--block')?.getAttribute('data-type')
		if (blockType === 'image' || blockType === 'file') {
			return
		}

		if (event.key === 'Enter') {
			await typingHistoryManager.onEnter()
			hasCompositionContent = false
		}

		if (event.key === 'Backspace' || event.key === 'Delete') {
			await typingHistoryManager.onDelete(event.key)

			if (selector.value) {
				const selection = window.getSelection()
				if (selection && selection.rangeCount > 0) {
					const range = selection.getRangeAt(0)
					const isAtStart = (range.startOffset === 0 || range.startOffset === 1) && range.collapsed

					if (event.key === 'Backspace' && isAtStart) {
						const prevBlock = selector.value.parentElement?.previousElementSibling?.querySelector('.editor--block')

						if (prevBlock) {
							await typingHistoryManager.saveCurrentState()
						}
					}

					if (event.key === 'Delete') {
						const useSelection = new UseSelection()
						const isAtEnd = useSelection.isCursorAtBlockEnd(selector.value)

						if (isAtEnd) {
							const nextBlock = selector.value.parentElement?.nextElementSibling?.querySelector('.editor--block')

							if (nextBlock) {
								await typingHistoryManager.saveCurrentState()
							}
						}
					}
				}
			}
		} else if (!isFormattingShortcut && (event.key.length === 1 || event.key === 'Process')) {
			await typingHistoryManager.onRegularKey()
		}

		if (isFormattingShortcut) {
			if (hasCompositionContent) {
				hasCompositionContent = false
			}
			await typingHistoryManager.saveCurrentState()
			await history.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
		}

		await blockEvent(event)
		socketSave()

		// 타이핑 중 DOM 조작/소켓 이벤트로 인해 active가 빠질 수 있으므로 복원
		if (selector.value?.dataset?.type === 'text' && !selector.value.classList.contains('active')) {
			selector.value.classList.add('active')
		}
	}
	
	const cleanup = () => {
		if (compositionTimeout) {
			clearTimeout(compositionTimeout)
			compositionTimeout = null
		}
		if (mutationObserver) {
			mutationObserver.disconnect()
			mutationObserver = null
		}
		typingHistoryManager.cleanup()
	}
	
	return {
		handleBeforeInput,
		handleInput,
		handleCompositionStart,
		handleCompositionEnd,
		forceEndCompositionIfNeeded,
		handleGlobalUndo,
		changeInput,
		setupOrphanedFontSizeObserver,
		cleanup
	}
}
